/**
 * GP-1359 — why a "Find Patient" result is missing from the To Be Billed - IV queue.
 *
 * The Find Patient panel queries `iv_events` in AUDIT mode (`includeConverted=true`
 * + `includeAllVisitStatuses=true`), so it deliberately returns events the queue
 * itself drops — above all the ones already converted to a live Postgres order.
 * The panel renders the raw NEXI `visitStatus`, which stays "Scheduled" forever,
 * so a visit that was converted AND delivered still reads as an upcoming booking.
 * The reconciled "Order" column carries the truth, but it can be hidden by the
 * user's saved grid view (`useQueueGrid` persists `columnVisibilityModel`), and
 * then the two views look like they disagree about the same visit.
 *
 * These helpers derive an always-visible explanation from the `orderLinks` the
 * backend already overlays on every returned row, so the panel can state plainly
 * that an event is out of the queue because it became an order.
 */
import {
  selectRepresentativeLink,
  tierForLink,
  type OrderLink,
  type OrderLinkTier,
} from './orderLinkTier';

/**
 * Client mirror of backend `isLiveOrderLink()` (v2-orders.service.ts). The queue
 * hides a visit only when it has a non-cancelled AND non-archived order link —
 * cancelled-only / archived-only visits stay re-orderable and stay in the queue,
 * so they must NOT be reported as converted here.
 */
export function isLiveOrderLink(
  link: Pick<OrderLink, 'isCancelled' | 'isArchived'>,
): boolean {
  return !link.isCancelled && !link.isArchived;
}

/**
 * Turn a raw workflow stage ('out_for_delivery') into a label ('Out For Delivery').
 *
 * Deliberately NOT `WORKFLOW_STAGE_LABELS` (constants/troubleshootingWorkflow.ts):
 * that map only covers the stages with a troubleshooting queue and has no entry for
 * `ready_for_pickup`, `picked_up` or `cancelled` — which are precisely the dispatched
 * stages a converted visit lands in, so a lookup would render `undefined` for them.
 * A total string transform can't drift out of sync with the stage enum; this is not a
 * second copy of that map.
 */
export function humanizeWorkflowStage(stage: string | undefined | null): string {
  if (!stage) return '';
  return stage
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export interface ConvertedPatientEvent {
  /** NEXI visit id of the event (webhook-numeric or `MANUAL-…`). */
  visitId: string;
  /** The live order the visit became. */
  orderNumber: string;
  /** Raw workflow stage of that order, e.g. 'delivered'. */
  workflowStage: string;
  /** Display label for the stage, e.g. 'Delivered'. */
  stageLabel: string;
  /** Tier of the representative link — drives the chip colour. */
  tier: OrderLinkTier;
  /** The representative link itself, for rendering an `OrderLinkChip`. */
  link: OrderLink;
}

/**
 * Events in the patient panel that are already converted to a LIVE order and are
 * therefore (correctly) absent from the To Be Billed - IV queue.
 *
 * Derived purely from the reconciled Postgres `orderLinks` — never from the Mongo
 * `status`/`convertedToBilling` fields, which `upsertIVEvent` can regress back to
 * 'pending' on a later webhook (see nexi-visit-listing.controller.ts).
 */
export function summarizeConvertedPatientEvents(
  events: Array<{ visitId?: string; orderLinks?: OrderLink[] }> | undefined | null,
): ConvertedPatientEvent[] {
  if (!events || events.length === 0) return [];

  const converted: ConvertedPatientEvent[] = [];
  for (const event of events) {
    const liveLinks = (event.orderLinks ?? []).filter(isLiveOrderLink);
    const representative = selectRepresentativeLink(liveLinks);
    if (!representative) continue;

    converted.push({
      visitId: event.visitId ?? '',
      orderNumber: representative.orderNumber,
      workflowStage: representative.workflowStage,
      stageLabel: humanizeWorkflowStage(representative.workflowStage),
      tier: tierForLink(representative),
      link: representative,
    });
  }
  return converted;
}
